import * as React from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";
import { useHistory } from "react-router";

import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/core/shared/api";
import { Button } from "src/library/components/Button";
import {
  IPaymentCard,
  usePaymentApi
} from "src/modules/payment/api/payment.api";

interface RemoveBranchModalProps extends Omit<BasicModalProps, "title"> {
  terminology: ITerminology;
  paymentMethod: IPaymentCard;
}

/**
 * Modal hanlding deleting a payment method
 */
const DeletePaymentMethodModal: React.ReactFCWithChildren<RemoveBranchModalProps> = ({
  open,
  onDismiss,
  terminology,
  paymentMethod
}) => {
  const [ deletePaymentMethodRes, deletePaymentMethodReq ] = usePaymentApi("DELETE_PAYMENT_METHOD");
  const history = useHistory();

  React.useEffect(() => {
    if (deletePaymentMethodRes.error) {
      toast.error(deletePaymentMethodRes.error.message);
    }

    if (deletePaymentMethodRes.data) {
      history.push("/settings/user#payment_methods");
      toast.success("Successfully deleted payment method.");
    }
  }, [ deletePaymentMethodRes, history ]);

  // stop user from attempting to delete method if card still has drops associated
  const handleDeletePaymentMethod = React.useCallback(() => {
    if (paymentMethod.branchPaymentCards && paymentMethod.branchPaymentCards.length > 0) {
      toast.error(`This card cannot be deleted because it has ${terminology.drop.plural.text} associated with it. Please remove them and try again.`);

      return;
    }

    deletePaymentMethodReq(paymentMethod.id);
  }, [
    deletePaymentMethodReq,
    paymentMethod,
    terminology.drop.plural.text
  ]);

  return (
    <StyledRemoveBranchModal
      open={open}
      onDismiss={onDismiss}
      title={`Remove ${_.startCase(terminology.drop.singular.text)} Association`}
    >
      <>
        <div className="message">
          <Text>
            Are you sure that you want to delete this card?
          </Text>
        </div>

        <div className="actions">
          <Button
            disabled={deletePaymentMethodRes.pending}
            onClick={() => onDismiss()}
            $buttonType="secondary"
          >
            No, cancel
          </Button>

          <Button
            loading={deletePaymentMethodRes.pending}
            $buttonType="primary"
            onClick={handleDeletePaymentMethod}
          >
            Yes, remove
          </Button>
        </div>
      </>
    </StyledRemoveBranchModal>
  );
};

export default DeletePaymentMethodModal;

const StyledRemoveBranchModal = styled(BasicModal)`
  ${({ theme }) => css`
    max-width: 440px;

    .message {
      margin-bottom: ${theme.spacing.margins.lg};
    }

    .actions {
      display: flex;
      justify-content: space-between;
    }
  `}
`;
